import { useCallback, useId, useState } from 'react';

import { useFathomDisconnect } from '@/hooks/integrations/meeting-notes';
import type { SourceStatus } from '@/hooks/integrations/meeting-summaries-sources-status';
import { isSourceConnected } from '@/hooks/integrations/meeting-summaries-sources-status';
import { Button } from '@/lib/base/buttons/button';
import { AlertFloating, ConfirmationDialog } from '@/lib/base/overlays';
import { apiGet } from '@/lib/http/client';
import { DEFAULT_FATHOM_ERROR_MESSAGE } from '@/lib/integrations/meeting-summaries/error-messages';
import { setOAuthInstanceCookie } from '@/lib/oauth/oauthInstanceCookie';

export interface FathomActionsProps {
  orgId: number;
  status: SourceStatus;
  canConnect: boolean;
  canModify: boolean;
}

interface InstallUrlResponse {
  url: string;
}

function getFathomInstallUrl(orgId: number): string {
  return `/api/orgs/${orgId}/integrations/meeting-notes/fathom/install/`;
}

export function FathomActions({
  orgId,
  status,
  canConnect,
  canModify,
}: FathomActionsProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const {
    mutateAsync: disconnectAsync,
    isPending: isDisconnecting,
    isError: isDisconnectError,
    error: disconnectError,
  } = useFathomDisconnect(orgId);

  const connectHelpId = useId();
  const disconnectHelpId = useId();

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    try {
      const data = await apiGet<InstallUrlResponse>(getFathomInstallUrl(orgId));
      if (data?.url) {
        // Local-proxy only: route the OAuth callback back to this instance.
        setOAuthInstanceCookie();
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Failed to fetch Fathom install URL:', err);
      setConnectError(DEFAULT_FATHOM_ERROR_MESSAGE);
    }
  }, [orgId]);

  const handleDisconnectClick = useCallback(() => setIsConfirmOpen(true), []);

  const handleConfirmDisconnect = useCallback(async () => {
    await disconnectAsync();
    setIsConfirmOpen(false);
  }, [disconnectAsync]);

  const handleCancelDisconnect = useCallback(() => setIsConfirmOpen(false), []);

  const errorMessage =
    connectError ??
    (isDisconnectError
      ? (disconnectError?.message ?? DEFAULT_FATHOM_ERROR_MESSAGE)
      : null);

  const isConnected = isSourceConnected(status);
  const isAuthFailed = status.connection_status === 'auth_failed';
  const reconnectAllowed = isAuthFailed ? canModify : canConnect;

  return (
    <div className='space-y-2'>
      {errorMessage && (
        <div role='alert'>
          <AlertFloating
            color='error'
            title='Something went wrong'
            description={errorMessage}
          />
        </div>
      )}

      {isConnected ? (
        <div className='space-y-1'>
          <Button
            color='secondary'
            size='sm'
            isDisabled={!canModify || isDisconnecting}
            isLoading={isDisconnecting}
            aria-describedby={!canModify ? disconnectHelpId : undefined}
            onClick={handleDisconnectClick}
          >
            Disconnect
          </Button>
          {!canModify && (
            <p
              id={disconnectHelpId}
              className='text-xs text-[var(--color-text-secondary)]'
            >
              Only admins can disconnect integrations
            </p>
          )}
        </div>
      ) : (
        <div className='space-y-1'>
          <Button
            color='primary'
            size='sm'
            isDisabled={!reconnectAllowed}
            aria-describedby={!reconnectAllowed ? connectHelpId : undefined}
            onClick={handleConnect}
          >
            {isAuthFailed ? 'Reconnect' : 'Connect'}
          </Button>
          {!reconnectAllowed && (
            <p
              id={connectHelpId}
              className='text-xs text-[var(--color-text-secondary)]'
            >
              {isAuthFailed
                ? 'Only admins can reconnect integrations'
                : 'Only admins can connect integrations'}
            </p>
          )}
        </div>
      )}

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        variant='warning'
        title='Disconnect Fathom'
        description='Meeting summaries will no longer be captured. You can reconnect any time.'
        confirmLabel='Disconnect'
        isLoading={isDisconnecting}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </div>
  );
}
